import { Link } from "react-router-dom";
import { Mail, MessageSquareText, Lightbulb, ShieldCheck, LifeBuoy, FileText } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import { FaqList } from "@/components/sections/Faq";
import Support from "@/components/sections/Support";
import WhatsAppCTA from "@/components/sections/WhatsAppCTA";
import SupportProject from "@/components/sections/SupportProject";
import { Reveal } from "@/lib/motion";

const SUPPORT_MAIL = "support@festivalplan.app";
const WA_URL = "https://wa.me/4915225477425";
const DELETE_SUBJECT = encodeURIComponent("Anfrage zur Löschung meiner FestivalPlan-Daten");

const FAQS = [
  {
    q: "Wie erstelle ich eine Crew?",
    a: "In der App tippst du auf das '+'-Symbol im Bereich 'Gruppen' und legst eine neue Crew mit Namen und Festival an. Anschließend kannst du weitere Mitglieder einladen.",
  },
  {
    q: "Wie kann ich einer Crew beitreten?",
    a: "Bitte den Ersteller der Crew, dich einzuladen. Über die Einladung wirst du der Gruppe hinzugefügt und siehst alle gemeinsamen Inhalte.",
  },
  {
    q: "Wie funktionieren Aufgaben?",
    a: "Im Bereich 'Aufgaben' der jeweiligen Crew kannst du Aufgaben anlegen, Personen zuweisen und den Status (offen, in Bearbeitung, erledigt) verfolgen.",
  },
  {
    q: "Wie funktioniert die Mitbringliste?",
    a: "Öffne die Mitbringliste einer Crew, füge Artikel hinzu und übernimm jene, die du mitbringst. Der Fortschritt ist für alle Mitglieder sichtbar.",
  },
  {
    q: "Wie funktioniert die Gruppenkasse?",
    a: "Trage Ausgaben mit Betrag und beteiligten Personen ein. FestivalPlan berechnet automatisch, wer wem etwas schuldet.",
  },
  {
    q: "Wie funktionieren Autos und Fahrgemeinschaften?",
    a: "Im Bereich 'Autos' können Fahrer ihr Fahrzeug anlegen und Mitfahrer hinzufügen. So behaltet ihr Sitzplätze, Treffpunkt und Uhrzeit im Blick.",
  },
  {
    q: "Wie funktioniert die Karte?",
    a: "Über die Kartenfunktion kannst du – nach ausdrücklicher Standortfreigabe – Marker (z. B. Camp, Bühne, Treffpunkt) setzen und Laufwege aufzeichnen. Inhalte sind ausschließlich für Mitglieder der jeweiligen Crew sichtbar.",
  },
  {
    q: "Was kann ich tun, wenn ich mich nicht anmelden kann?",
    a: "Stelle sicher, dass E-Mail und Passwort korrekt sind und dein Gerät online ist. Prüfe außerdem, ob eine neuere App-Version verfügbar ist. Kommst du weiterhin nicht rein, schreib uns an support@festivalplan.app.",
  },
  {
    q: "Was kann ich tun, wenn Nachrichten oder Benachrichtigungen nicht funktionieren?",
    a: "Prüfe die Benachrichtigungs-Einstellungen deines Geräts sowie die Berechtigungen für FestivalPlan. Ein Neustart der App bzw. des Geräts hilft häufig. Sollte das Problem bestehen bleiben, melde dich bitte beim Support.",
  },
];

function CTAButton({ href, children, variant = "primary", testId }) {
  const base = "inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-all duration-300";
  const cls = variant === "primary" ? `${base} btn-primary` : `${base} btn-ghost`;
  return (
    <a href={href} data-testid={testId} className={cls}>
      {children}
    </a>
  );
}

function InfoBlock({ Icon, color, title, children, actions, testId }) {
  return (
    <div data-testid={testId} className="grid grid-cols-1 gap-6 py-12 lg:grid-cols-[1fr_1.6fr] lg:gap-20">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-white/10" style={{ background: `${color}14` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </span>
        <h2 className="font-display text-2xl font-semibold text-white">{title}</h2>
      </div>
      <div>
        <p className="text-base leading-relaxed text-white/65">{children}</p>
        {actions && <div className="mt-6 flex flex-wrap gap-3">{actions}</div>}
      </div>
    </div>
  );
}

export default function SupportPage() {
  return (
    <div data-testid="support-page">
      <PageIntro
        eyebrow="Support"
        title="FestivalPlan Support"
        lead="Wir helfen dir bei Fragen, Problemen und Feedback rund um FestivalPlan."
      >
        <div data-testid="support-contact-options" className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
          <a href={`mailto:${SUPPORT_MAIL}`} data-testid="support-email-final" className="inline-flex min-h-[44px] items-center gap-2 text-white transition-colors hover:text-[#00E5FF]">
            <Mail className="h-4 w-4 text-[#00E5FF]" />
            {SUPPORT_MAIL}
          </a>
          <a href={WA_URL} target="_blank" rel="noopener noreferrer" data-testid="support-whatsapp-link" className="inline-flex min-h-[44px] items-center gap-2 text-white transition-colors hover:text-[#22C55E]">
            <MessageSquareText className="h-4 w-4 text-[#22C55E]" />
            WhatsApp: +49 1522 5477425
          </a>
        </div>
      </PageIntro>

      <Support />

      <section data-testid="support-faq-section" className="border-b border-white/10">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-5 py-20 lg:grid-cols-[1fr_1.6fr] lg:gap-20 lg:px-8 lg:py-24">
          <Reveal>
            <p className="mb-3 text-xs uppercase tracking-[0.25em] text-[#8B5CF6]">Hilfe</p>
            <h2 className="font-display text-3xl font-bold tracking-tighter text-white sm:text-4xl">Häufige Fragen zur App</h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-white/60">
              Antworten zu Crews, Aufgaben, Mitbringliste, Gruppenkasse, Autos, Karte und Anmeldung.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <FaqList items={FAQS} prefix="support-faq" valuePrefix="sf" />
          </Reveal>
        </div>
      </section>

      <section data-testid="support-info-section" className="border-b border-white/10">
        <div className="mx-auto max-w-6xl divide-y divide-white/10 px-5 lg:px-8">
          <InfoBlock Icon={LifeBuoy} color="#8B5CF6" title="Technische Probleme?" testId="support-block-tech"
            actions={<>
              <CTAButton href={`mailto:${SUPPORT_MAIL}?subject=${encodeURIComponent("Technisches Problem in FestivalPlan")}`} testId="support-contact-tech">
                <MessageSquareText className="h-4 w-4" /> Support kontaktieren
              </CTAButton>
              <CTAButton href={`mailto:${SUPPORT_MAIL}`} variant="ghost" testId="support-email">
                <Mail className="h-4 w-4" /> E-Mail senden
              </CTAButton>
            </>}
          >
            Wenn FestivalPlan bei dir nicht wie erwartet funktioniert, beschreibe dein Problem
            möglichst genau. Hilfreich sind dabei dein Gerät, deine Betriebssystemversion, die
            verwendete App-Version und eine kurze Beschreibung des Problems.
          </InfoBlock>

          <InfoBlock Icon={Lightbulb} color="#00E5FF" title="Feedback & Verbesserungsvorschläge" testId="support-block-feedback"
            actions={
              <CTAButton href={`mailto:${SUPPORT_MAIL}?subject=${encodeURIComponent("Feedback zu FestivalPlan")}`} testId="support-feedback">
                <MessageSquareText className="h-4 w-4" /> Feedback senden
              </CTAButton>
            }
          >
            FestivalPlan wird laufend weiterentwickelt. Du hast einen Verbesserungsvorschlag,
            eine Idee für eine neue Funktion oder möchtest uns einfach Feedback geben? Schreib
            uns gerne.
          </InfoBlock>

          <InfoBlock Icon={ShieldCheck} color="#22C55E" title="Datenschutz & deine Daten" testId="support-block-privacy"
            actions={<>
              <Link to="/datenschutz" data-testid="support-link-datenschutz" className="btn-primary inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium">
                <FileText className="h-4 w-4" /> Datenschutzerklärung
              </Link>
              <CTAButton href={`mailto:${SUPPORT_MAIL}?subject=${DELETE_SUBJECT}`} variant="ghost" testId="support-delete-data">
                <Mail className="h-4 w-4" /> Datenlöschung anfragen
              </CTAButton>
            </>}
          >
            FestivalPlan verarbeitet bestimmte Daten, die für die Bereitstellung der
            App-Funktionen erforderlich sind. Weitere Informationen darüber, welche Daten
            verarbeitet werden, zu welchen Zwecken sie verwendet werden und welche Rechte dir
            zustehen, findest du in unserer Datenschutzerklärung.
          </InfoBlock>

          <InfoBlock Icon={FileText} color="#FACC15" title="Impressum" testId="support-block-impressum">
            Anbieterangaben findest du auf unserer{" "}
            <Link to="/impressum" data-testid="support-link-impressum" className="text-[#00E5FF]">Impressumsseite</Link>.
          </InfoBlock>
        </div>
      </section>

      <WhatsAppCTA />
      <SupportProject />
    </div>
  );
}
